import { useState, useEffect, useCallback, useRef } from 'react';
import { VIDEO_CONFIG, AUDIO_CONFIG, MEDIA_STATES } from '@shared/utils/constants';
import { classifyMediaError, checkBrowserSupport } from '@shared/utils/helpers';
import { useInterview } from '@features/interview/context/InterviewContext';

export const useMedia = () => {
  const { state, actions } = useInterview();
  const streamRef = useRef(null);
  const [devices, setDevices] = useState({ cameras: [], microphones: [] });
  const [selectedCamera, setSelectedCamera] = useState(null);
  const [selectedMicrophone, setSelectedMicrophone] = useState(null);
  const [permissionStatus, setPermissionStatus] = useState({
    camera: 'prompt',
    microphone: 'prompt',
  });

  // Check browser support
  const browserSupport = checkBrowserSupport();

  // Restore stream reference from context state on mount
  useEffect(() => {
    if (state.mediaStream && !streamRef.current) {
      streamRef.current = state.mediaStream;
    }
  }, [state.mediaStream]);

  // Get available devices
  const getDevices = useCallback(async () => {
    try {
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      const cameras = deviceList.filter(device => device.kind === 'videoinput');
      const microphones = deviceList.filter(device => device.kind === 'audioinput');
      setDevices({ cameras, microphones });
      
      // Set default devices
      if (cameras.length > 0 && !selectedCamera) {
        setSelectedCamera(cameras[0].deviceId);
      }
      if (microphones.length > 0 && !selectedMicrophone) {
        setSelectedMicrophone(microphones[0].deviceId);
      }
      
      return { cameras, microphones };
    } catch (error) {
      console.error('Error enumerating devices:', error);
      return { cameras: [], microphones: [] };
    }
  }, [selectedCamera, selectedMicrophone]);

  // Check permissions
  const checkPermissions = useCallback(async () => {
    try {
      if (navigator.permissions) {
        const [cameraPermission, micPermission] = await Promise.all([
          navigator.permissions.query({ name: 'camera' }).catch(() => ({ state: 'prompt' })),
          navigator.permissions.query({ name: 'microphone' }).catch(() => ({ state: 'prompt' })),
        ]);
        
        setPermissionStatus({
          camera: cameraPermission.state,
          microphone: micPermission.state,
        });
        
        return {
          camera: cameraPermission.state,
          microphone: micPermission.state,
        };
      }
    } catch (error) {
      console.error('Error checking permissions:', error);
    }
    return { camera: 'prompt', microphone: 'prompt' };
  }, []);

  // Request media stream
  const requestMedia = useCallback(async (options = {}) => {
    const {
      video = true,
      audio = true,
      cameraId = selectedCamera,
      microphoneId = selectedMicrophone,
    } = options;

    if (!browserSupport.isSupported) {
      const error = {
        type: 'browser',
        key: 'BROWSER_NOT_SUPPORTED',
        message: 'متصفحك لا يدعم واجهات الوسائط المطلوبة',
      };
      actions.setError(error);
      actions.setMediaState(MEDIA_STATES.ERROR);
      return null;
    }

    actions.setMediaState(MEDIA_STATES.REQUESTING);

    try {
      // Stop existing stream
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }

      const constraints = {
        video: video ? {
          deviceId: cameraId ? { exact: cameraId } : undefined,
          width: { ideal: VIDEO_CONFIG.WIDTH },
          height: { ideal: VIDEO_CONFIG.HEIGHT },
          frameRate: { ideal: VIDEO_CONFIG.FRAME_RATE },
          facingMode: 'user',
        } : false,
        audio: audio ? {
          deviceId: microphoneId ? { exact: microphoneId } : undefined,
          sampleRate: AUDIO_CONFIG.SAMPLE_RATE,
          channelCount: AUDIO_CONFIG.CHANNEL_COUNT,
          echoCancellation: AUDIO_CONFIG.ECHO_CANCELLATION,
          noiseSuppression: AUDIO_CONFIG.NOISE_SUPPRESSION,
          autoGainControl: AUDIO_CONFIG.AUTO_GAIN_CONTROL,
        } : false,
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      actions.setMediaStream(stream);
      actions.setMediaState(MEDIA_STATES.ACTIVE);
      
      // Refresh device list after getting permissions
      await getDevices();
      await checkPermissions();
      
      return stream;
    } catch (error) {
      console.error('Error requesting media:', error);
      const classifiedError = classifyMediaError(error);
      
      if (classifiedError.type === 'permission') {
        actions.setMediaState(MEDIA_STATES.DENIED);
      } else {
        actions.setMediaState(MEDIA_STATES.ERROR);
      }
      
      actions.setError({
        ...classifiedError,
        message: error.message,
        originalError: error,
      });
      
      return null;
    }
  }, [browserSupport.isSupported, selectedCamera, selectedMicrophone, actions, getDevices, checkPermissions]);

  // Stop media stream
  const stopMedia = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    actions.setMediaStream(null);
    actions.setMediaState(MEDIA_STATES.IDLE);
  }, [actions]);

  // Toggle camera - actually stops/starts the camera hardware
  const toggleCamera = useCallback(async () => {
    if (streamRef.current) {
      const newEnabledState = !state.cameraEnabled;

      if (newEnabledState) {
        // Turn camera ON - request new video track
        try {
          const newStream = await navigator.mediaDevices.getUserMedia({
            video: {
              deviceId: selectedCamera ? { exact: selectedCamera } : undefined,
              width: { ideal: VIDEO_CONFIG.WIDTH },
              height: { ideal: VIDEO_CONFIG.HEIGHT },
              frameRate: { ideal: VIDEO_CONFIG.FRAME_RATE },
              facingMode: 'user',
            },
          });

          // Add new video track to existing stream
          const newVideoTrack = newStream.getVideoTracks()[0];

          // Remove old video tracks
          streamRef.current.getVideoTracks().forEach(track => {
            streamRef.current.removeTrack(track);
          });

          // Add new video track
          streamRef.current.addTrack(newVideoTrack);

          // Update context state
          actions.toggleCamera();
          actions.setMediaStream(streamRef.current);
        } catch (error) {
          console.error('Failed to turn camera on:', error);
        }
      } else {
        // Turn camera OFF - stop video tracks to turn off camera light
        const videoTracks = streamRef.current.getVideoTracks();
        videoTracks.forEach(track => {
          track.stop(); // This turns off the camera hardware/light
          streamRef.current.removeTrack(track);
        });

        // Update context state
        actions.toggleCamera();
        actions.setMediaStream(streamRef.current);
      }
    }
  }, [actions, state.cameraEnabled, selectedCamera]);

  // Toggle microphone
  const toggleMicrophone = useCallback(() => {
    if (streamRef.current) {
      const audioTracks = streamRef.current.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      actions.toggleMicrophone();
    }
  }, [actions]);

  // Switch camera
  const switchCamera = useCallback(async (deviceId) => {
    setSelectedCamera(deviceId);
    if (state.mediaState === MEDIA_STATES.ACTIVE) {
      await requestMedia({ cameraId: deviceId });
    }
  }, [state.mediaState, requestMedia]);

  // Switch microphone
  const switchMicrophone = useCallback(async (deviceId) => {
    setSelectedMicrophone(deviceId);
    if (state.mediaState === MEDIA_STATES.ACTIVE) {
      await requestMedia({ microphoneId: deviceId });
    }
  }, [state.mediaState, requestMedia]);

  // Listen for device changes
  useEffect(() => {
    const handleDeviceChange = async () => {
      await getDevices();
    };

    navigator.mediaDevices?.addEventListener('devicechange', handleDeviceChange);
    return () => {
      navigator.mediaDevices?.removeEventListener('devicechange', handleDeviceChange);
    };
  }, [getDevices]);

  // Cleanup on unmount
  // Note: We don't stop the stream here because it needs to persist across page navigation
  // The stream should only be stopped when explicitly calling stopMedia() or when the user logs out
  useEffect(() => {
    return () => {
      // Cleanup is handled by explicit stopMedia() calls
      // Don't stop tracks on component unmount to preserve stream across navigation
    };
  }, []);

  return {
    // State
    stream: streamRef.current || state.mediaStream,
    mediaState: state.mediaState,
    isActive: state.mediaState === MEDIA_STATES.ACTIVE,
    cameraEnabled: state.cameraEnabled,
    microphoneEnabled: state.microphoneEnabled,
    devices,
    selectedCamera,
    selectedMicrophone,
    permissionStatus,
    browserSupport,
    error: state.error,
    
    // Actions
    requestMedia,
    stopMedia,
    toggleCamera,
    toggleMicrophone,
    switchCamera,
    switchMicrophone,
    getDevices,
    checkPermissions,
  };
};

export default useMedia;
